import { Endpoints } from "@netmountains/shared/browser";
import { accountAdded } from "redux/reducers/account.js";

import apiClient from "../api/apiClient.js";

// Create uppercase constants for API endpoints
const API_ENDPOINTS = {
  AUTH: {
    LOGIN: Endpoints.auth.login,
    LOGOUT: Endpoints.auth.logout,
    REFRESH: Endpoints.auth.refreshToken,
    REGISTER: Endpoints.auth.register,
  },
};

/**
 * Authentifizierungsdienst für das Frontend
 */
export const authService = {
  /**
   * Nutzer anmelden
   * @param {Object} credentials - Anmeldedaten (E-Mail und Passwort)
   * @returns {Promise<Object>} Antwort mit Nutzer- und Token-Daten
   */
  login: async credentials => {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN, credentials);
    const { success, data } = response;

    if (success && data && data.user) {
      // Store user object in localStorage for session persistence
      localStorage.setItem("user", JSON.stringify(data.user));
    }

    return response;
  },

  /**
   * Nutzer abmelden
   * @returns {Promise<Object>} Abmeldebestätigung
   */
  logout: async () => {
    try {
      // Call logout endpoint - cookies will be cleared by the server
      const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);

      // Clear user from localStorage
      localStorage.removeItem("user");

      return response.data;
    } catch (error) {
      console.error("Logout fehlgeschlagen:", error);

      // Ensure user is cleared even if logout API call fails
      localStorage.removeItem("user");

      throw error;
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} True if authenticated, false otherwise
   */
  isAuthenticated: () => {
    // Check for user data in localStorage
    const storedUser = localStorage.getItem("user");
    return !!storedUser;

    // Note: With HTTP-only cookies, we can't directly check if cookies exist
    // We rely on the stored user data as a proxy for authentication status
  },

  /**
   * Get the current authenticated user
   * This helps ensure Redux is properly synced
   * @param {Function} dispatch - Redux dispatch function (optional)
   * @returns {Object|null} User data if authenticated, null otherwise
   */
  getCurrentUser: dispatch => {
    try {
      // Check for stored user (lightweight, synchronous operation)
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const user = JSON.parse(storedUser);

        // Update Redux store if dispatch is provided
        if (dispatch && user) {
          dispatch(accountAdded(user));
        }

        return user;
      }
      return null;
    } catch (error) {
      console.error("Error getting current user:", error);
      return null;
    }
  },

  /**
   * Authentifizierungsstatus überwachen
   * @param {Function} callback - Funktion, die aufgerufen wird, wenn sich der Auth-Status ändert
   * @returns {Function} Funktion zum Entfernen des Event-Listeners
   */
  observeAuthStatus: callback => {
    const handleStorageChange = event => {
      // Update based on user object instead of token
      if (event.key === "user") {
        callback(!!event.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  },
};
